/**
 * Inspection — Smart Property Inspection Copilot (paid landlords only).
 *
 * Landlord uploads move-in / move-out / periodic inspection photos +
 * a text note per room. Copilot returns a structured report:
 *   • Room-by-room condition rating (A/B/C/D)
 *   • Damage flags (nail holes, stains, missing hardware, mold risk)
 *   • Repair recommendations grouped by priority
 *   • Estimated fix cost band (CA$ low / mid / high)
 *   • Deposit-return recommendation with province-specific reasoning
 *
 * Backend touched: NONE. Uses the existing /api/copilot/assist endpoint
 * with a task-specific `context` string. If LLM budget is out, the
 * global fallback returns a template report so the flow doesn't
 * dead-end.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, ClipboardCheck, ShieldCheck, AlertTriangle, ArrowRight, Zap, Download, Sparkles, Plus, Trash2 } from "lucide-react";
import PhotoPicker from "@/components/PhotoPicker";
import { friendlyError } from "@/lib/friendlyError";
import { useAuth } from "@/lib/auth";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const ROOM_OPTIONS = [
  "Kitchen", "Living Room", "Bedroom", "Bathroom", "Basement",
  "Garage", "Exterior", "Balcony", "Laundry", "Hallway",
];

function newRoom(id, name = "") {
  return { id, name, photos: [], notes: "" };
}

export default function Inspection() {
  const { user } = useAuth() || {};
  const [inspectionType, setInspectionType] = useState("move-in");
  const [property, setProperty] = useState("");
  const [tenant, setTenant] = useState("");
  const [province, setProvince] = useState(user?.province || "ON");
  const [rooms, setRooms] = useState([newRoom(1, "Living Room"), newRoom(2, "Kitchen")]);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState("");
  const [err, setErr] = useState("");
  const [source, setSource] = useState("");

  const canSubmit = useMemo(() => {
    if (!property.trim() || !rooms.length) return false;
    return rooms.some((r) => r.name && (r.notes.trim() || r.photos.length));
  }, [property, rooms]);

  const addRoom = () => setRooms((r) => [...r, newRoom(Date.now())]);
  const removeRoom = (id) => setRooms((r) => r.filter((x) => x.id !== id));
  const updateRoom = (id, patch) => setRooms((r) => r.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const submit = async () => {
    setBusy(true);
    setErr("");
    setReport("");
    setSource("");
    // Build the free-form input the Copilot will analyze.
    const parts = [
      `INSPECTION TYPE: ${inspectionType}`,
      `PROPERTY: ${property}`,
      tenant ? `TENANT: ${tenant}` : null,
      `PROVINCE: ${province}`,
      "",
      "ROOMS:",
      ...rooms.filter((r) => r.name).map((r) => {
        return `• ${r.name}\n  Photos attached: ${r.photos.length}\n  Notes: ${r.notes || "(none)"}`;
      }),
    ].filter(Boolean).join("\n");

    const context = (
      `structured Canadian property-inspection report for a ${inspectionType} inspection in ${province}. ` +
      "For each room return: (1) condition rating A/B/C/D, (2) damage flags observed, (3) prioritized repair recommendations (URGENT / SOON / EVENTUAL), (4) estimated cost band in CAD (Low $0-200 / Mid $200-800 / High $800+). " +
      "End with a Deposit-Return Recommendation with province-specific reasoning (RTA/RTB/RTDRS/Loi 41 as applicable) and a total estimated repair cost. " +
      "Format the whole response as clean readable Markdown headings, not code fences. No AI mention."
    );

    try {
      const r = await fetch(`${API}/api/copilot/assist`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}` },
        body: JSON.stringify({ text: parts, mode: "expand", context }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok && !(j?.text || j?.body)) {
        setErr(friendlyError({ response: { status: r.status, data: j } }, "Copilot is briefly unavailable. Try again in a moment."));
      } else {
        const text = j.text || j.body || j.draft || "";
        setSource(j.source || "claude");
        if (j.source === "fallback" || (text && text.trim() === parts.trim())) {
          setErr("Copilot credits are topping up — real inspection reports typically return within a minute. Please retry in ~60 seconds.");
        } else {
          setReport(text || "");
        }
      }
    } catch (e) {
      setErr(friendlyError(e, "Copilot is briefly unavailable."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="inspection-page">
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <header>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-100 mb-3">
            <Sparkles className="w-3.5 h-3.5" /> Smart Inspection Copilot · Paid plan
          </div>
          <h1 className="text-3xl font-black tracking-tighter text-slate-900" data-testid="inspection-h1">
            Property inspection — in 60 seconds.
          </h1>
          <p className="mt-2 text-slate-600 max-w-2xl">
            Walk each room, snap photos, jot a short note. The Copilot writes a structured, deposit-defensible report you can hand to a tenant, tribunal, or vendor without touching a template.
          </p>
        </header>

        {/* Form */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-7 shadow-sm space-y-5">
          <div className="grid sm:grid-cols-3 gap-4">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Inspection type</span>
              <select value={inspectionType} onChange={(e) => setInspectionType(e.target.value)}
                data-testid="inspection-type-select"
                className="mt-1 w-full p-2 border border-slate-300 rounded-md text-sm bg-white">
                <option value="move-in">Move-in</option>
                <option value="move-out">Move-out</option>
                <option value="periodic">Periodic (6-month)</option>
                <option value="damage-claim">Damage claim</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Property address</span>
              <input value={property} onChange={(e) => setProperty(e.target.value)} placeholder="123 Bloor W, Toronto"
                data-testid="inspection-property-input"
                className="mt-1 w-full p-2 border border-slate-300 rounded-md text-sm" />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Province</span>
              <select value={province} onChange={(e) => setProvince(e.target.value)}
                data-testid="inspection-province-select"
                className="mt-1 w-full p-2 border border-slate-300 rounded-md text-sm bg-white">
                {["ON","BC","AB","QC","MB","SK","NS","NB","NL","PE","YT","NT","NU"].map((p) => (<option key={p} value={p}>{p}</option>))}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Tenant name (optional)</span>
            <input value={tenant} onChange={(e) => setTenant(e.target.value)} placeholder="Alex Wong"
              data-testid="inspection-tenant-input"
              className="mt-1 w-full p-2 border border-slate-300 rounded-md text-sm" />
          </label>

          {/* Rooms */}
          <div className="border-t border-slate-100 pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold text-slate-900">Rooms inspected</span>
              <button onClick={addRoom} data-testid="inspection-add-room" className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900">
                <Plus className="w-3.5 h-3.5" /> Add room
              </button>
            </div>
            <div className="space-y-4">
              {rooms.map((r) => (
                <div key={r.id} className="p-4 rounded-lg border border-slate-200 bg-slate-50" data-testid={`inspection-room-${r.id}`}>
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <select value={r.name} onChange={(e) => updateRoom(r.id, { name: e.target.value })}
                      className="flex-1 p-2 border border-slate-300 rounded-md text-sm bg-white">
                      <option value="">— Pick a room —</option>
                      {ROOM_OPTIONS.map((n) => (<option key={n} value={n}>{n}</option>))}
                    </select>
                    <button onClick={() => removeRoom(r.id)} data-testid={`inspection-remove-room-${r.id}`} className="p-2 text-slate-400 hover:text-rose-600" title="Remove room">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Photos</div>
                      <PhotoPicker
                        value={r.photos}
                        onChange={(next) => updateRoom(r.id, { photos: next })}
                        testid={`inspection-photos-${r.id}`}
                        max={6}
                      />
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Notes</div>
                      <textarea
                        value={r.notes}
                        onChange={(e) => updateRoom(r.id, { notes: e.target.value })}
                        placeholder="Small chip in the countertop near sink. Faucet drips 1 drop/min. Fresh paint on north wall."
                        data-testid={`inspection-notes-${r.id}`}
                        className="w-full h-24 p-2 border border-slate-300 rounded-md text-sm bg-white"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Submit */}
          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <button
              onClick={submit}
              disabled={busy || !canSubmit}
              data-testid="inspection-submit"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Zap className="w-4 h-4" /> {busy ? "Copilot inspecting…" : "Generate inspection report"}
            </button>
            <div className="text-xs text-slate-500 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Report is stored on your account
            </div>
          </div>

          {err && (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm flex items-start gap-2" data-testid="inspection-err">
              <AlertTriangle className="w-4 h-4 mt-0.5" /> {err}
            </div>
          )}
        </div>

        {/* Report output */}
        {report && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-7 shadow-sm" data-testid="inspection-report">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-emerald-700">
                  Inspection report · {inspectionType} · {province} {source === "fallback" && "· template mode"}
                </div>
                <h2 className="text-2xl font-black tracking-tight text-slate-900 mt-1 flex items-center gap-2">
                  <ClipboardCheck className="w-6 h-6 text-emerald-600" /> {property}
                </h2>
              </div>
              <a
                href={`data:text/markdown;charset=utf-8,${encodeURIComponent(report)}`}
                download={`inspection-${property.replace(/\s+/g, "-").toLowerCase()}.md`}
                data-testid="inspection-download"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800"
              >
                <Download className="w-3.5 h-3.5" /> Download .md
              </a>
            </div>
            <article className="prose prose-slate max-w-none whitespace-pre-wrap text-sm leading-relaxed">
              {report}
            </article>
            <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
              <Link to="/tickets" data-testid="inspection-open-tickets" className="text-sm font-semibold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1">
                Open work tickets from this report <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <Link to="/leases" className="text-xs text-slate-500 hover:text-slate-800">
                Attach to lease →
              </Link>
            </div>
          </div>
        )}

        {/* Marketing footer */}
        <footer className="text-center text-xs text-slate-500 py-6 flex items-center justify-center gap-2">
          <Camera className="w-3.5 h-3.5" /> Every inspection is dated + geo-tagged for tribunal defensibility
        </footer>
      </div>
    </div>
  );
}
